/**
 * lead-form.js — Universal Lead Form Handler
 * ────────────────────────────────────────────
 * Configure via data-* attributes on the <form> element:
 *
 *   <form id="leadForm"
 *         action="/api/lead.php"
 *         data-project="axishub"
 *         data-budget-field="interest"
 *         data-fallback-phone="9911332635">
 *
 * Required field names in HTML form:
 *   name         — full name
 *   mobile       — 10-digit Indian mobile number
 *   interest     — OR budget (set via data-budget-field)
 *   company      — honeypot (hidden via CSS, must stay empty)
 *   csrf_token   — auto-included if present in form
 *
 * UTMs auto-captured from URL into hidden fields on page load.
 * form_load_time anti-bot timestamp auto-injected.
 * call_click / whatsapp_click GTM events auto-attached sitewide.
 */

document.addEventListener('DOMContentLoaded', function () {

  // ── Find form ────────────────────────────────────────────────
  const form = document.getElementById('leadForm');
  if (!form) return;

  // ── Config from data-* attributes ───────────────────────────
  const API_URL      = form.getAttribute('action') || '/api/lead.php';
  const PROJECT      = form.dataset.project        || '';
  const BUDGET_FIELD = form.dataset.budgetField    || 'budget';
  const FALLBACK_TEL = form.dataset.fallbackPhone  || '9999999999';

  // ── Elements ─────────────────────────────────────────────────
  const messageBox      = document.getElementById('formMessage');
  const submitBtn       = form.querySelector('button[type="submit"]');
  if (!submitBtn) return;

  const originalBtnHTML = submitBtn.innerHTML;
  let submitting        = false;

  // ── Anti-bot: inject form_load_time ─────────────────────────
  const pageLoadTime   = Date.now();
  const timeInput      = document.createElement('input');
  timeInput.type       = 'hidden';
  timeInput.name       = 'form_load_time';
  timeInput.value      = pageLoadTime;
  form.appendChild(timeInput);

  // ── UTMs: capture from URL → fill hidden fields ──────────────
  const urlParams = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(k => {
    const el = form.querySelector(`[name="${k}"]`) || document.getElementById(k);
    if (el) el.value = urlParams.get(k) || '';
  });

  // ── UI helpers ───────────────────────────────────────────────
  function showError(msg, field) {
    if (messageBox) {
      // Use inline styles with !important to override any Tailwind classes on the div
      messageBox.setAttribute('style',
        'display:block !important;background:#fee2e2;color:#991b1b;' +
        'border:1px solid #fecaca;border-radius:6px;padding:10px;' +
        'margin-bottom:10px;font-size:0.875rem;text-align:left;'
      );
      messageBox.innerHTML = '⚠️ ' + msg;
    }
    if (field) {
      field.style.borderColor = '#ef4444';
      field.focus();
    }
  }

  function clearErrors() {
    if (messageBox) {
      messageBox.setAttribute('style', 'display:none');
      messageBox.innerHTML = '';
    }
    // Reset any red borders
    form.querySelectorAll('input, select').forEach(el => {
      el.style.borderColor = '';
    });
  }

  // Spinner + label — match id="leadSpinner" and id="leadBtnLabel" in lead-form.php
  const spinner  = document.getElementById('leadSpinner');
  const btnLabel = document.getElementById('leadBtnLabel');

  function showSpinner() {
    submitting         = true;
    submitBtn.disabled = true;
    submitBtn.style.opacity = '0.85';
    if (spinner)  spinner.classList.remove('hidden');
    if (btnLabel) btnLabel.textContent = 'Submitting...';
  }

  function restoreButton() {
    submitting              = false;
    submitBtn.disabled      = false;
    submitBtn.style.opacity = '1';
    if (spinner)  spinner.classList.add('hidden');
    if (btnLabel) btnLabel.textContent = 'Get Details';
  }

  function showSuccess() {
    submitBtn.disabled      = true;
    submitBtn.style.opacity = '1';
    submitBtn.style.background = '#16a34a';
    submitBtn.style.color      = '#000';
    if (spinner)  spinner.classList.add('hidden');
    if (btnLabel) btnLabel.textContent = 'Thank you! Redirecting you...';
  }

  function isRepeatNumber(num) {
    return /^(\d)\1{9}$/.test(num)
        || num === '1234567890'
        || num === '9876543210';
  }

  // ── Submit handler ───────────────────────────────────────────
  form.addEventListener('submit', async function (e) {
    e.preventDefault();
    if (submitting) return;

    clearErrors();

    // Honeypot — silent reject
    const honeypot = form.querySelector('[name="company"]') || form.querySelector('[name="website"]');
    if (honeypot?.value?.trim()) return;

    // Anti-bot timing
    if (Date.now() - pageLoadTime < 3000) {
      showError('Please wait a moment before submitting.');
      return;
    }

    // Read fields
    const nameField   = form.querySelector('[name="name"]');
    const mobileField = form.querySelector('[name="mobile"]');
    const budgetField = form.querySelector(`[name="${BUDGET_FIELD}"]`);

    const name   = nameField?.value.trim()   || '';
    const mobile = mobileField?.value.trim() || '';
    const budget = budgetField?.value        || '';

    // Validate
    if (name.length < 2) {
      showError('Please enter a valid name.', nameField);
      return;
    }
    if (!/^[6-9][0-9]{9}$/.test(mobile)) {
      showError('Please enter a valid 10-digit mobile number.', mobileField);
      return;
    }
    if (isRepeatNumber(mobile)) {
      showError('Invalid mobile number.', mobileField);
      return;
    }
    if (!budget) {
      showError('Please select an option.', budgetField);
      return;
    }

    submitting = true;
    showSpinner();

    // Build FormData from entire form — gets all hidden fields automatically
    const fd = new FormData(form);
    if (PROJECT) fd.set('project', PROJECT);

    // Ensure budget is sent under the standard 'budget' key
    // so lead.php finds it via $_POST['budget'] regardless of field name in HTML
    fd.set('budget', budget);

    fd.set('pageUrl', window.location.href);

    // POST to API
    try {
      const response = await fetch(API_URL, { method: 'POST', body: fd });
      const result   = await response.json();

      if (result.status === 'error') {
        showError(result.message || 'Something went wrong. Please try again.');
        restoreButton();
        return;
      }

      if (!response.ok) {
        throw new Error(result.message || 'Server error.');
      }

      // Success — redirect to thank-you page
      showSuccess();

      setTimeout(() => {
        const baseUrl = result.thankYouUrl || '/thank-you.php';
        const dest    = new URL(baseUrl, window.location.origin);

        // Pass name + mobile for personalisation on thank-you page
        dest.searchParams.set('n', name);
        dest.searchParams.set('p', '+91 ' + mobile);

        // Preserve UTMs on redirect
        urlParams.forEach((v, k) => {
          if (!dest.searchParams.has(k)) dest.searchParams.set(k, v);
        });
        
        if (window.dataLayer) dataLayer.push({ event: 'form_submit', project: PROJECT, source: 'inline' });

        window.location.href = dest.toString();
      }, 1500);

    } catch (err) {
      console.error('Lead submit error:', err);
      showError(
        `Something went wrong. Please call us on <a href="tel:${FALLBACK_TEL}">${FALLBACK_TEL}</a>.`
      );
      restoreButton();
    }
  });

  // ── GTM click tracking — auto-attached sitewide ──────────────
  document.querySelectorAll('a[href^="tel:"]').forEach(el => {
    el.addEventListener('click', function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'call_click' });
    });
  });

  document.querySelectorAll('a[href*="wa.me"]').forEach(el => {
    el.addEventListener('click', function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'whatsapp_click' });
    });
  });

});